import React, { useRef, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { PageHeader } from '../../components/admin/PageHeader';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { A4Frame } from '../../components/notice/A4Frame';
import { SecureNotice, bodyPlaceholders, defaultBody, defaultTemplate, type NoticeTemplate } from '../../components/notice/SecureNotice';
import { toBn } from '../../utils/bangla';
const versions = [{
  v: 'T-2025.06 (v3)',
  status: 'সক্রিয়',
  date: '১০/০৬/২০২৫'
}, {
  v: 'T-2025.05 (v2)',
  status: 'নিষ্ক্রিয়',
  date: '২২/০৫/২০২৫'
}, {
  v: 'T-2025.04 (v1)',
  status: 'নিষ্ক্রিয়',
  date: '০৩/০৪/২০২৫'
}];
export function TemplateManagement() {
  const [t, setT] = useState<NoticeTemplate>(defaultTemplate);
  const set = <K extends keyof NoticeTemplate,>(k: K, v: NoticeTemplate[K]) => setT(s => ({
    ...s,
    [k]: v
  }));
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const insertToken = (token: string) => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? t.bodyText.length;
    const end = el?.selectionEnd ?? t.bodyText.length;
    const next = t.bodyText.slice(0, start) + token + t.bodyText.slice(end);
    set('bodyText', next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };
  const Toggle = ({
    k,
    label
  }: {
    k: 'showLogo' | 'showInstructions' | 'showSignature';
    label: string;
  }) => <label className="flex cursor-pointer items-center justify-between rounded-md border border-border px-3 py-2.5 text-sm" data-id="element-1711" data-mp="17c54rgmww77w">
      {label}
      <button type="button" role="switch" aria-checked={t[k]} onClick={() => set(k, !t[k])} className={`relative h-5 w-9 rounded-full transition-colors ${t[k] ? 'bg-[#16834A]' : 'bg-slate-300'}`} data-id="element-1712" data-mp="2isvdp7ipzd57">
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${t[k] ? 'left-[18px]' : 'left-0.5'}`} data-id="element-1713" data-mp="2uv644jl2hl2b" />
      </button>
    </label>;
  return <div data-id="element-1714" data-mp="2zv284itrlchv">
      <PageHeader screen="WEB SCREEN 16" title="নোটিশ টেমপ্লেট ব্যবস্থাপনা" subtitle="পরিবর্তন সংরক্ষণের পর নতুন টেমপ্লেট সংস্করণ অনুমোদন সাপেক্ষে সক্রিয় হবে" actions={<>
            <Button variant="outline" onClick={() => setT(defaultTemplate)} data-id="element-1716" data-mp="2c4oieyynjpqg"><RotateCcw className="h-4 w-4" data-id="element-1717" data-mp="2c4y8emjp5ww5" /> রিসেট</Button>
            <Button onClick={() => toast.success('নতুন টেমপ্লেট সংস্করণ খসড়া হিসেবে সংরক্ষিত', {
        description: 'T-2025.10 (v4) • সুপার অ্যাডমিনের অনুমোদন প্রয়োজন'
      })} data-id="element-1718" data-mp="2h3ht6jmk12mx"><Save className="h-4 w-4" data-id="element-1719" data-mp="2lwazhbxo8j14" /> সংস্করণ সংরক্ষণ</Button>
          </>} data-id="element-1715" data-mp="2zs6f98wrxe0a" />
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]" data-id="element-1720" data-mp="1oczv5x0otrgd">
        <div className="space-y-6" data-id="element-1721" data-mp="1td5f0r0pu9nw">
          <Panel title="সরকারি হেডার ও লোগো" data-id="element-1722" data-mp="1tc42ei41xf42">
            <Toggle k="showLogo" label="ডিএমপি লোগো প্রদর্শন" data-id="element-1723" data-mp="1luxss0mi2nwx" />
            <label className="mt-3 block text-sm" data-id="element-1724" data-mp="1lsbktzvp66e6">
              <span className="mb-1 block font-medium" data-id="element-1725" data-mp="1gwmfth0wrxah">হেডার লাইন (প্রতি লাইনে একটি)</span>
              <Textarea rows={5} value={t.headerLines.join('\n')} onChange={e => set('headerLines', e.target.value.split('\n'))} data-id="element-1726" data-mp="1bvgnvlf2oe20" />
            </label>
          </Panel>
          <Panel title="স্মারক ও মামলার বিবরণ" data-id="element-1727" data-mp="12vju55dtsp03">
            <label className="block text-sm" data-id="element-1728" data-mp="12xd1yo06df9o">
              <span className="mb-1 block font-medium" data-id="element-1729" data-mp="y53dzdimepa1">স্মারক ফরম্যাট</span>
              <Input value={t.memoFormat} onChange={e => set('memoFormat', e.target.value)} data-id="element-1730" data-mp="y30hcamypxwc" />
              <span className="mt-1 block text-xs text-slate-500" data-id="element-1731" data-mp="t3w8xm7v4472">{'{memo}'} স্বয়ংক্রিয়ভাবে প্রতিস্থাপিত হবে</span>
            </label>
            <div className="mt-3 rounded-md bg-[#F4F7FB] p-3 text-xs text-slate-600" data-id="element-1732" data-mp="j8cel48y5dkf">মামলার তথ্য (Case Details) টেবিল: দুই কলাম — কেস নং, তারিখ, সময়, ধরন, গাড়ি নং | মালিক, মোবাইল, স্থান, ধারা, শুনানির তারিখ ও সময় (১১টি ক্ষেত্র)</div>
          </Panel>
          <Panel title="নোটিশের মূল অংশ (Body)" subtitle="বিষয়, মূল বক্তব্য ও উল্লেখ্য অংশ সম্পাদনা" actions={<Button size="xs" variant="ghost" onClick={() => setT(s => ({
          ...s,
          ...defaultBody
        }))} data-id="element-1734" data-mp="v4tis0onu9ia"><RotateCcw className="h-3.5 w-3.5" data-id="element-1735" data-mp="z65bwg6qtcv8" /> ডিফল্ট</Button>} data-id="element-1733" data-mp="13xnc320xz9f5">
            <label className="block text-sm" data-id="element-1736" data-mp="z42c54fsg5db">
              <span className="mb-1 block font-medium" data-id="element-1737" data-mp="l5uh522bbe4s">বিষয়</span>
              <Input value={t.subject} onChange={e => set('subject', e.target.value)} data-id="element-1738" data-mp="l8qhsy5jogwx" />
            </label>
            <label className="mt-3 block text-sm" data-id="element-1739" data-mp="c5pef582d3wy">
              <span className="mb-1 flex items-center justify-between font-medium" data-id="element-1740" data-mp="c70ofms3r7wi">
                মূল বক্তব্য
                <span className="text-xs font-normal text-slate-500" data-id="element-1741" data-mp="g7kph1fsqvnx">{toBn(t.bodyText.length)} অক্ষর</span>
              </span>
              <textarea ref={bodyRef} rows={9} value={t.bodyText} onChange={e => set('bodyText', e.target.value)} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-relaxed shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#1769AA]" data-id="element-1742" data-mp="2albu1mj9qpci" />
              <span className="mt-1 block text-xs text-slate-500" data-id="element-1743" data-mp="8puwcysy580n">নতুন অনুচ্ছেদের জন্য একটি খালি লাইন রাখুন।</span>
            </label>
            <div className="mt-2" data-id="element-1744" data-mp="ilg31a8ymwgq">
              <div className="mb-1.5 text-xs font-medium text-slate-600" data-id="element-1745" data-mp="ikmvw863a4y2">মামলার তথ্য যুক্ত করুন (কার্সরের অবস্থানে বসবে)</div>
              <div className="flex flex-wrap gap-1.5" data-id="element-1746" data-mp="doz2fgkkqpmr">
                {bodyPlaceholders.map(p => <button key={p.token} type="button" onClick={() => insertToken(p.token)} className="rounded-full border border-[#1769AA]/40 bg-[#E8F1FA] px-2.5 py-1 text-xs text-[#0f4c80] hover:bg-[#d6e7f6]" data-id="element-1747" data-mp="rk9w4qn314pj">
                    + {p.label}
                  </button>)}
              </div>
            </div>
            <label className="mt-4 block text-sm" data-id="element-1748" data-mp="xam985st3961">
              <span className="mb-1 block font-medium" data-id="element-1749" data-mp="xbe41eekx2v9">উল্লেখ্য অংশ (বোল্ড)</span>
              <Textarea rows={2} value={t.noteText} onChange={e => set('noteText', e.target.value)} placeholder="খালি রাখলে প্রদর্শিত হবে না" data-id="element-1750" data-mp="1295xicuvgx1k" />
            </label>
          </Panel>
          <Panel title="QR, নির্দেশনা ও স্বাক্ষর" data-id="element-1751" data-mp="1bbf62x6kygcb">
            <fieldset data-id="element-1752" data-mp="1b8j8xb2k3vox">
              <legend className="mb-2 text-sm font-medium" data-id="element-1753" data-mp="1g9gp5i40afns">QR অবস্থান</legend>
              <div className="grid grid-cols-2 gap-2" data-id="element-1754" data-mp="1g7dq68kvlc6x">
                {([['top-right', 'প্রাপকের পাশে (ডানে)'], ['bottom-left', 'নিচে বাম (যোগাযোগ অংশে)']] as const).map(([v, l]) => <label key={v} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${t.qrPlacement === v ? 'border-[#003B71] bg-[#E8F1FA]/50' : 'border-border'}`} data-id="element-1755" data-mp="1q1mgcpmfot1x">
                    <input type="radio" className="accent-[#003B71]" checked={t.qrPlacement === v} onChange={() => set('qrPlacement', v)} data-id="element-1756" data-mp="1yy57pxf0o8we" /> {l}
                  </label>)}
              </div>
            </fieldset>
            <div className="mt-3 space-y-2" data-id="element-1757" data-mp="3ahar6gd7rzkc">
              <Toggle k="showInstructions" label="নাগরিক নির্দেশনা" data-id="element-1758" data-mp="3aidl6x5u2i4h" />
              <Toggle k="showSignature" label="স্বাক্ষর ব্লক" data-id="element-1759" data-mp="35j99nzvfs8az" />
            </div>
            <label className="mt-3 block text-sm" data-id="element-1760" data-mp="634aeaa55il6">
              <span className="mb-1 block font-medium" data-id="element-1761" data-mp="62uj8jdd15dw">ফুটার</span>
              <Input value={t.footer} onChange={e => set('footer', e.target.value)} data-id="element-1762" data-mp="21inrx7t1pxs" />
            </label>
          </Panel>
          <Panel title="টেমপ্লেট সংস্করণ" bodyClassName="divide-y divide-border" data-id="element-1763" data-mp="3tf56fc4xr7b6">
            {versions.map(v => <div key={v.v} className="flex items-center justify-between px-5 py-3 text-sm" data-id="element-1764" data-mp="3oh3hiaktwdrs">
                <div data-id="element-1765" data-mp="pso5yj17wub9"><div className="font-mono font-medium" data-id="element-1766" data-mp="ptg0rrmzqo0h">{v.v}</div><div className="text-xs text-slate-500" data-id="element-1767" data-mp="l0d4hbqtx0gl">{v.date}</div></div>
                <StatusBadge label={v.status} data-id="element-1768" data-mp="3pig1yy9zv7rb" />
              </div>)}
          </Panel>
        </div>
        <Panel title="লাইভ A4 প্রিভিউ" bodyClassName="bg-slate-200/70 p-4 md:p-6" data-id="element-1769" data-mp="3u8whcnxdl3gb">
          <div className="mx-auto max-w-[794px] shadow-lg xl:sticky xl:top-20" data-id="element-1770" data-mp="3fka1ml3u69ok">
            <A4Frame data-id="element-1771" data-mp="3fi729agsn476"><SecureNotice template={t} data-id="element-1772" data-mp="3fifer19087hu" /></A4Frame>
          </div>
        </Panel>
      </div>
    </div>;
}